import { useCallback, useEffect, useState } from "react";
import { Search, Loader2, X, Ban, ShieldCheck, Save, KeyRound, Download } from "lucide-react";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { toast } from "../../../components/ui/sonner";

function formatDate(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export default function UsersTab() {
  const { authFetch, downloadCsv } = useAdminAuth();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full user detail
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const path = `/api/admin/export/users.csv${params.toString() ? `?${params}` : ""}`;
      const name = await downloadCsv(path, "geonetix-users.csv");
      toast("CSV downloaded", { description: name });
    } catch (e) {
      toast("Export failed", { description: e.message });
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(
    async (query = "") => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query) params.set("q", query);
        params.set("limit", "50");
        const data = await authFetch(`/api/admin/users?${params.toString()}`);
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch {
        /* handled */
      } finally {
        setLoading(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    load("");
  }, [load]);

  const openDetail = async (uid) => {
    setDetailLoading(true);
    setSelected({ user: { id: uid, loading: true } });
    try {
      const data = await authFetch(`/api/admin/users/${uid}`);
      setSelected(data);
    } catch (e) {
      toast("Failed to load user", { description: e.message });
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          load(q);
        }}
        className="flex gap-3"
      >
        <div className="flex-1 relative">
          <Search size={15} className="absolute top-1/2 -translate-y-1/2 left-3 text-slate-500" />
          <input
            data-testid="users-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, name, referral or device"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00B4D8]/60"
          />
        </div>
        <button
          data-testid="users-search-submit"
          type="submit"
          className="inline-flex items-center gap-2 rounded-xl bg-[#00B4D8] px-5 py-2.5 text-sm font-semibold text-[#04140a] hover:brightness-110"
        >
          Search
        </button>
        <button
          data-testid="users-export-csv"
          type="button"
          onClick={doExport}
          disabled={exporting}
          title="Download the full users list as CSV (respects current search)"
          className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-slate-200 hover:brightness-110 disabled:opacity-60"
        >
          {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
          CSV
        </button>
      </form>

      <div className="text-xs text-slate-500">
        {loading ? "Loading…" : `${total} user${total === 1 ? "" : "s"}`}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <p data-testid="users-empty" className="text-slate-500 text-center py-16">
          No users found.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table data-testid="users-table" className="w-full min-w-[960px] text-sm">
            <thead className="bg-white/[0.03] text-left text-slate-400 uppercase text-[11px] tracking-[0.15em]">
              <tr>
                <th className="px-4 py-3">Name / Email</th>
                <th className="px-4 py-3">Device</th>
                <th className="px-4 py-3">Date of Signup</th>
                <th className="px-4 py-3">Device Assigned</th>
                <th className="px-4 py-3">Referral</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="users-table-body">
              {items.map((u) => (
                <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-white">{u.full_name || "—"}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300 font-mono-gx text-xs">{u.device_number || "—"}</td>
                  <td data-testid={`user-signup-date-${u.id}`} className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap">
                    {formatDate(u.created_at) || "—"}
                  </td>
                  <td data-testid={`user-device-date-${u.id}`} className="px-4 py-3 text-xs whitespace-nowrap">
                    {formatDate(u.device_generated_at) ? (
                      <span className="text-slate-300">{formatDate(u.device_generated_at)}</span>
                    ) : (
                      <span className="text-slate-500 italic">Not Assigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-300 font-mono-gx text-xs">{u.referral_id || "—"}</td>
                  <td className="px-4 py-3 text-slate-300 capitalize text-xs">{u.role || "user"}</td>
                  <td className="px-4 py-3">
                    {u.blocked ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-red-400/40 bg-red-400/10 px-2 py-0.5 text-[10px] font-bold text-red-300">
                        <Ban size={10} /> BLOCKED
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#4CAF50]/40 bg-[#4CAF50]/10 px-2 py-0.5 text-[10px] font-bold text-[#4CAF50]">
                        <ShieldCheck size={10} /> ACTIVE
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`user-open-${u.id}`}
                      onClick={() => openDetail(u.id)}
                      className="text-[#00B4D8] hover:underline text-xs font-semibold"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <UserDetailDrawer
          data={selected}
          loading={detailLoading}
          onClose={() => setSelected(null)}
          onRefresh={() => {
            if (selected?.user?.id) openDetail(selected.user.id);
            load(q);
          }}
        />
      )}
    </div>
  );
}

function UserDetailDrawer({ data, loading, onClose, onRefresh }) {
  const { authFetch } = useAdminAuth();
  const [saving, setSaving] = useState(false);
  const [device, setDevice] = useState("");
  const [edit, setEdit] = useState({ full_name: "", per_km_rate: "", role: "" });

  useEffect(() => {
    if (data?.user && !data.user.loading) {
      setDevice(data.user.device_number || "");
      setEdit({
        full_name: data.user.full_name || "",
        per_km_rate: data.user.per_km_rate ?? "",
        role: data.user.role || "user",
      });
    }
  }, [data]);

  if (!data) return null;
  const user = data.user || {};

  const runAction = async (path, opts = {}) => {
    setSaving(true);
    try {
      const res = await authFetch(path, { method: "POST", body: JSON.stringify(opts.body || {}) });
      toast("Done", { description: opts.successText || "Saved" });
      onRefresh();
      return res;
    } catch (e) {
      toast("Action failed", { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const body = {};
      if (edit.full_name !== user.full_name) body.full_name = edit.full_name;
      const kmr = parseFloat(edit.per_km_rate);
      if (!Number.isNaN(kmr) && kmr !== user.per_km_rate) body.per_km_rate = kmr;
      if (edit.role && edit.role !== user.role) body.role = edit.role;
      if (Object.keys(body).length === 0) {
        toast("Nothing to save");
        return;
      }
      await authFetch(`/api/admin/users/${user.id}`, { method: "PATCH", body: JSON.stringify(body) });
      toast("User updated");
      onRefresh();
    } catch (e) {
      toast("Update failed", { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  const assignDevice = async () => {
    if (!device.trim()) return;
    setSaving(true);
    try {
      await authFetch(`/api/admin/users/${user.id}/assign-device`, {
        method: "POST",
        body: JSON.stringify({ device_number: device.trim() }),
      });
      toast("Device assigned", { description: device.trim() });
      onRefresh();
    } catch (e) {
      toast("Assign failed", { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00B4D8]/60";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end">
      <div data-testid="user-detail-drawer" className="w-full max-w-lg h-full overflow-y-auto bg-[#0A192F] border-l border-white/10">
        <div className="sticky top-0 bg-[#0A192F] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">{user.full_name || user.email || "User"}</div>
            <div className="text-xs text-slate-500">{user.email}</div>
          </div>
          <button data-testid="user-drawer-close" onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        {loading || user.loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <section>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">Edit</h3>
              <div className="space-y-3">
                <label className="block">
                  <span className="mb-1 block text-xs text-slate-400">Full name</span>
                  <input value={edit.full_name} onChange={(e) => setEdit({ ...edit, full_name: e.target.value })} className={inputCls} />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs text-slate-400">Per km rate (₹)</span>
                  <input type="number" step="0.1" value={edit.per_km_rate} onChange={(e) => setEdit({ ...edit, per_km_rate: e.target.value })} className={inputCls} />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs text-slate-400">Role</span>
                  <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} className={inputCls}>
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                    <option value="staff">staff</option>
                  </select>
                </label>
                <button
                  data-testid="user-save-btn"
                  onClick={saveEdits}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-[#4CAF50] px-4 py-2 text-xs font-semibold text-[#04140a] hover:brightness-110 disabled:opacity-60"
                >
                  <Save size={13} /> Save changes
                </button>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">Dates</h3>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-sm font-bold">{formatDate(user.created_at) || "—"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Signup</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-sm font-bold">{formatDate(user.device_generated_at) || "Not Assigned"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Device Assigned</div>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">Device</h3>
              <div className="flex gap-2">
                <input value={device} onChange={(e) => setDevice(e.target.value)} placeholder="GNX-XXXX-XXXX-XXXX" className={`${inputCls} font-mono-gx`} />
                <button
                  data-testid="user-assign-device-btn"
                  onClick={assignDevice}
                  disabled={saving || !device.trim()}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#00B4D8] px-4 py-2 text-xs font-semibold text-[#04140a] hover:brightness-110 disabled:opacity-60"
                >
                  <KeyRound size={13} /> Assign
                </button>
              </div>
              {user.device_number && (
                <p className="text-xs text-slate-500 mt-2">
                  Current: <span className="font-mono-gx text-slate-300">{user.device_number}</span>
                </p>
              )}
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">Status</h3>
              {user.blocked ? (
                <button
                  data-testid="user-unblock-btn"
                  onClick={() => runAction(`/api/admin/users/${user.id}/unblock`, { successText: "User unblocked" })}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full border border-[#4CAF50]/50 bg-[#4CAF50]/10 px-4 py-2 text-xs font-semibold text-[#4CAF50] hover:brightness-110 disabled:opacity-60"
                >
                  <ShieldCheck size={13} /> Unblock user
                </button>
              ) : (
                <button
                  data-testid="user-block-btn"
                  onClick={() => runAction(`/api/admin/users/${user.id}/block`, { successText: "User blocked" })}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full border border-red-400/50 bg-red-400/10 px-4 py-2 text-xs font-semibold text-red-300 hover:brightness-110 disabled:opacity-60"
                >
                  <Ban size={13} /> Block user
                </button>
              )}
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">Recent activity</h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-lg font-black font-mono-gx">{(data.withdrawals || []).length}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Withdrawals</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-lg font-black font-mono-gx">{(data.mapping_submissions || []).length}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Mappings</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-lg font-black font-mono-gx">{(data.network_sessions || []).length}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Sessions</div>
                </div>
              </div>
              <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs text-slate-400 font-mono-gx">
                total_earnings: ₹{Number(user.total_earnings || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">KYC</h3>
              {data.kyc ? (
                <pre className="max-h-56 overflow-auto rounded-xl border border-white/10 bg-black/40 p-3 text-[11px] text-slate-300 whitespace-pre-wrap break-all font-mono-gx">
                  {JSON.stringify(data.kyc, null, 2)}
                </pre>
              ) : (
                <p className="text-xs text-slate-500">No KYC on record.</p>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
